import { AnyAction, SidePanelLayerType } from "@nova/constants"
import { withRoot } from "@nova/with-root"
import {
    createAsyncThunk,
    createSelector,
    createSlice,
    isAnyOf,
    PayloadAction,
} from "@reduxjs/toolkit"
import {
    addLayerToSelection,
    clearSelection,
    removeLayerFromSelection,
    selectLastSelectedLayerId,
    selectSelectedLayers,
    selectSingleLayer,
} from "panels/content/side-panel/slice.new"
import { Text } from "@nova/protobuf"
import { updateText } from "features/pubsub/entityActions/cubes/text"
import { RootState } from "integrations/redux"
import { selectTextsMap } from "features/entity-caching/texts/selectors"

interface InitialStateType {
    isCreating: boolean
    isEditing: boolean
    isDragging: boolean
    dimensions?: { height: number; width: number }
    localText?: Text
}

const name = "text-v1"

const initialState: InitialStateType = {
    isCreating: false,
    isEditing: false,
    isDragging: false,
    dimensions: undefined,
}

export const slice = createSlice({
    name: name,
    initialState,
    reducers: {
        setTextDimensions: (
            state,
            action: PayloadAction<{ id: string; dimensions: { height: number; width: number } }>
        ) => {
            state.dimensions = action.payload.dimensions
        },
        setIsEditingText: (state, action: PayloadAction<boolean>) => {
            state.isEditing = action.payload
        },
        setIsDraggingText: (state, action: PayloadAction<boolean>) => {
            state.isDragging = action.payload
        },
        setLocalText: (state, action: { payload: Text | undefined }) => {
            state.localText = action.payload
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(
            isAnyOf(
                selectSingleLayer,
                addLayerToSelection,
                removeLayerFromSelection,
                clearSelection
            ),
            (state, action) => {
                const { id: layerId } = action.payload || { id: "" }

                // If the selected layer changed from the current one to a new one, wipe the local state.
                if (layerId !== state.localText?.id) {
                    // state.localText = undefined
                    state.isEditing = false
                }
            }
        )
    },
})

// Selectors
const { selectIsEditingText, selectIsDraggingText, selectLocalText } = withRoot(
    (r: AnyAction) => r[name],
    {
        selectIsEditingText: () => (s) => s.isEditing as boolean,
        selectIsDraggingText: () => (s) => s.isDragging as boolean,
        selectLocalText: () => (s) => s.localText as Text | undefined,
    }
)

const selectActiveTextId = createSelector(
    [selectLastSelectedLayerId, selectSelectedLayers],
    (lastSelectedLayerId, selectedLayers) =>
        selectedLayers.find((layer) => layer.id === lastSelectedLayerId)?.type ===
        SidePanelLayerType.Text
            ? lastSelectedLayerId
            : undefined
)

export { selectActiveTextId, selectIsEditingText, selectIsDraggingText, selectLocalText }
export const { setTextDimensions, setIsEditingText, setIsDraggingText, setLocalText } =
    slice.actions
export const reducer = { [name]: slice.reducer }

const placeholderNameRegex = /^Text (\d+)$/

// the layer name follows the content until the user gives it one of their own
export const deriveTextName = (currentName: string, startingContent: string, newContent: string) =>
    placeholderNameRegex.test(currentName) || currentName === startingContent
        ? newContent
        : currentName

export const publishLocalText = createAsyncThunk(`${name}/publishLocalText`, (_, thunkApi) => {
    const state = thunkApi.getState() as RootState
    const localText = selectLocalText()(state)
    const textsById = selectTextsMap(state)

    if (!localText) return

    // Make sure that the entity linked to localText has not been deleted.
    const text = textsById[localText.id]
    if (text) {
        thunkApi.dispatch(
            updateText({
                id: localText.id,
                updates: {
                    text: localText.text,
                    name: deriveTextName(text.name, text.text, localText.text),
                },
            })
        )
    }

    // Drop the draft either way — an orphaned one would seed the next text layer.
    thunkApi.dispatch(setLocalText(undefined))
})
